import React from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const MembershipSelectionStep = ({ formData, updateFormData, onNext, onPrevious }) => {
  const membershipPlans = [
    {
      id: 'explorador',
      name: 'Explorador',
      price: 20000,
      priceFormatted: '$20.000',
      period: 'COP/mes',
      icon: 'Compass',
      color: 'bg-gradient-to-br from-blue-500 to-blue-700',
      borderColor: 'border-blue-500',
      iconBg: 'bg-blue-100',
      iconColor: 'text-blue-700',
      features: [
        'Acceso a la plataforma',
        'Escaneo QR',
        '500 Notificaciones',
        'Historial de descuentos',
        'Soporte por Email'
      ],
      scanLimit: 'Ilimitado',
      discountLimit: 'Ideal para empezar',
      notificationQuota: '15 descuentos/mes'
    },
    {
      id: 'emprendedor',
      name: 'Emprendedor',
      price: 50000,
      priceFormatted: '$50.000',
      period: 'COP/mes',
      icon: 'TrendingUp',
      color: 'bg-gradient-to-br from-accent to-orange-600',
      borderColor: 'border-accent',
      iconBg: 'bg-orange-100',
      iconColor: 'text-orange-700',
      popular: true,
      features: [
        'Acceso a la plataforma',
        'Escaneo QR',
        '2000 Notificaciones',
        'Historial de descuentos',
        'Programa Bizznet Rewards',
        'Soporte prioritario'
      ],
      scanLimit: 'Ilimitado',
      discountLimit: 'Para Usuarios Activos',
      notificationQuota: '50 descuentos/mes'
    },
    {
      id: 'elite',
      name: 'Elite',
      price: 70000,
      priceFormatted: '$70.000',
      period: 'COP/mes',
      icon: 'Crown',
      color: 'bg-gradient-to-br from-primary to-purple-700',
      borderColor: 'border-primary',
      iconBg: 'bg-purple-100',
      iconColor: 'text-purple-700',
      features: [
        'Acceso a la plataforma',
        'Notificaciones ilimitadas',
        'Escaneo QR',
        'Programa Bizznet Rewards',
        'Historial de descuentos',
        'Soporte 24/7 dedicado'
      ],
      scanLimit: 'Ilimitado',
      discountLimit: 'Experiencia Premium',
      notificationQuota: 'descuentos Ilimitados'
    }
  ];

  const handleSelectPlan = (plan) => {
    updateFormData({ selectedPlan: plan });
  };

  const handleContinue = () => {
    if (formData?.selectedPlan) {
      onNext();
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-foreground mb-2">Selecciona Tu Plan de Membresía</h2>
        <p className="text-muted-foreground">Elige el plan que mejor se adapte a tus necesidades</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {membershipPlans?.map((plan) => (
          <div
            key={plan?.id}
            className={`relative bg-white rounded-2xl border-4 transition-all duration-300 cursor-pointer ${
              formData?.selectedPlan?.id === plan?.id
                ? `${plan?.borderColor} shadow-2xl transform scale-105`
                : 'border-gray-200 hover:border-gray-300 hover:shadow-lg'
            } ${plan?.popular ? 'lg:transform lg:scale-110 lg:z-10' : ''}`}
            onClick={() => handleSelectPlan(plan)}
          >
            {plan?.popular && (
              <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-accent text-white px-6 py-1.5 rounded-full text-sm font-bold shadow-lg">
                🔥 Más Popular
              </div>
            )}

            {formData?.selectedPlan?.id === plan?.id && (
              <div className="absolute top-4 right-4 bg-primary text-white rounded-full p-2 shadow-lg">
                <Icon name="Check" size={20} />
              </div>
            )}

            <div className="p-6 space-y-4">
              <div className="flex flex-col items-center text-center">
                <div className={`w-16 h-16 ${plan?.iconBg} ${plan?.iconColor} rounded-xl flex items-center justify-center mb-3 shadow-md`}>
                  <Icon name={plan?.icon} size={32} />
                </div>
                <h3 className="text-2xl font-bold text-foreground">{plan?.name}</h3>
              </div>

              <div className="text-center">
                <div className="flex items-baseline justify-center gap-1">
                  <span className="text-4xl font-extrabold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                    {plan?.priceFormatted}
                  </span>
                  <span className="text-sm text-muted-foreground font-semibold">{plan?.period}</span>
                </div>
              </div>

              <div className="space-y-2">
                <div className={`${plan?.color} text-white rounded-lg p-3 text-center shadow-md`}>
                  <p className="text-sm font-bold">{plan?.discountLimit}</p>
                </div>
                <div className="bg-green-50 border-2 border-green-200 rounded-lg p-3 text-center">
                  <p className="text-sm font-bold text-green-800">
                    {plan?.notificationQuota}
                  </p>
                </div>
              </div>

              <ul className="space-y-2">
                {plan?.features?.map((feature, index) => (
                  <li key={index} className="flex items-start gap-2 text-sm">
                    <div className="flex-shrink-0 mt-0.5">
                      <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center">
                        <Icon name="Check" size={12} color="#16a34a" />
                      </div>
                    </div>
                    <span className="text-foreground">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        ))}
      </div>

      <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <Icon name="Info" size={20} className="text-blue-600 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-blue-900">
            <p className="font-semibold mb-1">Información importante</p>
            <ul className="space-y-1 list-disc list-inside">
              <li>Todas las membresías tienen validez de 30 días desde la activación</li>
              <li>Puedes cambiar o cancelar tu plan en cualquier momento</li>
              <li>Los descuentos aplican según disponibilidad de cada negocio</li>
              <li>Las comisiones de afiliado se calculan sobre ventas referidas exitosas</li>
            </ul>
          </div>
        </div>
      </div>

      {formData?.selectedPlan && (
        <div className="bg-green-50 border-2 border-green-200 rounded-xl p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-green-800 font-semibold mb-1">Plan Seleccionado:</p>
              <p className="text-2xl font-bold text-green-900">{formData?.selectedPlan?.name}</p>
              <p className="text-lg text-green-700">{formData?.selectedPlan?.priceFormatted} COP/mes</p>
            </div>
            <div className="bg-green-200 text-green-800 rounded-full p-4">
              <Icon name="Check" size={32} />
            </div>
          </div>
        </div>
      )}

      <div className="flex gap-4 pt-6 border-t">
        <Button
          variant="outline"
          size="lg"
          onClick={onPrevious}
          iconName="ArrowLeft"
          iconPosition="left"
          className="flex-1"
        >
          Regresar
        </Button>

        <Button
          variant="default"
          size="lg"
          onClick={handleContinue}
          disabled={!formData?.selectedPlan}
          iconName="ArrowRight"
          iconPosition="right"
          className="flex-1 bg-gradient-to-r from-primary to-accent hover:shadow-lg transform hover:scale-105 transition-all"
        >
          Continuar al Pago
        </Button>
      </div>
    </div>
  );
};

export default MembershipSelectionStep;